<template>
  <div id="view-all-component">
    <sticky>
      <div class="clearfix header-panel">
        <div class="header">
          <span class="notice-header">
            {{ $i18n.t( 'tender.viewAll.title') }}
          </span>
        </div>
        <div class="right-btns">
          <ConfirmCreate
            v-if="showCreateButton"
            class="create-btn"
          />
          <button
            v-if="canExtendClosingDate"
            type="button"
            class="btn btn--darkblue"
            :disabled="!(closingDateSearch&&extendTenders.length)"
            @click="extendClosingDate"
          >
            {{ $t('action.extendClosingDate') }}
          </button>
          <HKLButton
            v-if="canExportSearchResults"
            color="orange"
            @click="exportTenders"
          >
            {{ $t('action.exportSearchResults') }}
          </HKLButton>
          <button
            type="button"
            class="btn btn--darkblue"
            @click="resetFilters"
          >
            {{ $t('action.resetFilter') }}
          </button>
          <button
            id="query-btn"
            type="button"
            class="btn btn--orange"
            @click="queryTenders"
          >
            {{ $t('action.search') }}
          </button>
        </div>
      </div>
    </sticky>
    <AdvanceSearchCollapse
      v-if="isHKlandUser"
      id="tender-advance-search-condition-group"
      class="advance-search-group"
    >
      <div class="invited-vendor">
        <label class="emphasis ad-search-condition-label">
          {{ $t('projectViewAll.invitedVendor') }}
        </label>
        <HKLInput
          v-model="invitedVendorName"
          @keydown.enter="queryTenders"
        />
      </div>
      <div class="issue-date-range">
        <label class="emphasis ad-search-condition-label">
          {{ $t('tender.issuedDate') }}
        </label>
        <HKLDateRange
          v-model="advancedDateRange.issuedDate"
          :canPutTotop="false"
          :placeholders="[$t('projectViewAll.startFrom'), $t('projectViewAll.until')]"
        />
      </div>
      <div class="closing-date-range">
        <label class="emphasis ad-search-condition-label">
          {{ $t('tender.closingDate') }}
        </label>
        <HKLDateRange
          v-model="advancedDateRange.closingDateRange"
          :displayRange="[{ to: null }, { to: null }]"
          :canPutTotop="false"
          :placeholders="[$t('projectViewAll.startFrom'), $t('projectViewAll.until')]"
        />
      </div>
      <div class="awarded-date-range">
        <label class="emphasis ad-search-condition-label">
          {{ $t('tender.awardedDate') }}
        </label>
        <HKLDateRange
          v-model="advancedDateRange.awardedDateRange"
          :canPutTotop="false"
          :placeholders="[$t('projectViewAll.startFrom'), $t('projectViewAll.until')]"
        />
      </div>
    </AdvanceSearchCollapse>

    <div class="view-all-component-tenders">
      <div>
        <span>{{ $t('total') }}: {{ displayTotalCount }}</span>
      </div>
      <CommonTable
        ref="view-all-component-tenders"
        :defaultOrderKey="defaultOrderKey"
        :orderByDateKeys="['awardedDate', 'closingDate']"
        :orderByNumberKeys="['awardedAmount', 'prNumber']"
        :renderHeaderList="itemHeaders"
        :tableId="tableId"
        :needSearch="true"
        :needCache="shouldUseTableCache"
        :yScrollAuto="true"
        :tableContentLists="parsedItems"
        :getDisplayListLength="getDisplayListLength"
        :defaultOrder="true"
        :interactionFunc="interactionFunc"
        :getConditions="getConditionsCallBack"
        :enterEvent="queryTenders"
        :disabledAllSearchFilter="isVendorHistory"
        :setTableInfo="setTableInfo"
        :getTableOrder="getTableOrder"
      />
    </div>

    <div
      id="extendClosingDateModal"
      ref="extendClosingDateModal"
      class="reveal"
      data-reveal
    >
      <button
        aria-label="Close modal"
        type="button"
        class="close-button"
        @click="closeAlertModal()"
      >
        <span aria-hidden="true">×</span>
      </button>
      <div class="tender-confirm clearfix modal-container">
        <label>{{ $t('action.extendTo') }}</label>
        <div
          id="edit-closing-date-picker"
          class="row"
        >
          <DatePicker
            v-model="selectedClosingDate"
            format="DD MMM YY"
            class="medium-5 small-5 large-5 columns"
            :class="invalidClosingDateClass"
            :disabled="disabledDates"
            :calendarButton="true"
            calendarButtonIcon="fa fa-calendar"
            @selected="val => updateClosingDate(val, selectedClosingTime)"
          />
          <HKLSelect
            class="medium-5 small-5 large-5 columns"
            :class="invalidClosingTimeClass"
            :value="selectedClosingTime || undefined"
            :options="closingTimeHklOptions"
            :showSearch="true"
            placeholder=""
            @change="val => updateClosingDate(selectedClosingDate, val || '')"
          />
        </div>
        <textarea
          v-model="extendClosingDateReason"
          v-autosize
          type="text"
          class="reason-box"
          :class="{ 'error-highlight': tryToConfirm&&!extendClosingDateReason }"
          placeholder="Please input the reason for extending closing date."
          required
        />

        <table>
          <thead>
            <tr>
              <th>{{ itemHeaders[0].name }}</th>
              <th>{{ itemHeaders[1].name }}</th>
              <th>{{ itemHeaders[2].name }}</th>
              <th>{{ itemHeaders[3].name }}</th>
              <th>{{ itemHeaders[4].name }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in extendTenders"
              :key="index"
            >
              <td>{{ item.general.prNumber }}</td>
              <td>{{ item.general.prDescription }}</td>
              <td>{{ item.general.site }}</td>
              <td>{{ parseStatusText(item.general) }}</td>
              <td>{{ item.general.closingDate && dateUtil.formatDate(item.general.closingDate, true) }}</td>
            </tr>
          </tbody>
        </table>
        <div class="row align-right nav_btn">
          <button
            type="button"
            class="btn btn--darkblue"
            @click="closeAlertModal()"
          >
            {{ $t('action.cancel') }}
          </button>
          <button
            type="button"
            class="btn btn--orange"
            @click="confirmExtend()"
          >
            {{ $t('action.ok') }}
          </button>
        </div>
      </div>
    </div>
    <HKLRemindModal ref="remindEmptyModal">
      <template slot="title">
        {{ $t('hint.emptySearch') }}
      </template>
    </HKLRemindModal>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { ref, getCurrentInstance, computed, unref } from 'vue-demi'
import currencyFormatter from 'currency-formatter'
import CommonTable from '@/controls/CommonTable'
import DatePicker from '@/controls/DatePicker'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import Sticky from '@/controls/Sticky'
import AdvanceSearchCollapse from '@/controls/AdvanceSearchCollapse'
import HKLDateRange from '@/controls/Date/HKLDateRange'
import HKLInput from '@/controls/Input/HKLInput'
import HKLMultiSelect, { ALL_VALUE } from '@/controls/Select/HKLMultiSelect'
import HKLInputOperatorNumber from '@/controls/Input/HKLInputOperatorNumber'
import dateUtil from '@/utils/dateUtil'
import notification from '@/utils/notification'
import userHelper from '@/utils/userHelper'
import { ADDENDUM_STATUS, EmailTypes, ActionTypes, tenderRouteNames } from '@/utils/consts'
import { dateFormat } from '@/utils/regex'
import { getPurchases, showQueryIndicator } from '@/api/tenders/util'
import { navToDetailPage, resolveNavToTenderDetailRouteName } from '@/utils/navigation'
import { parseStatusText, updateTender, exportAllTenders } from '@/api/tenders/request'
import { tenderStatus } from '@/utils/locale'
import { sendEmail } from '@/api/postman/request'
import { aclHelper, aclPermsType } from '@/utils/aclHelper'
import mutationTypes from '@/store/mutation-types'
import { DATA_CACHE_KEYS } from '@/store/util'
import { LIST_NAV_KIND } from '@/constants/listNavigation'
import { consumeResumeSearchQueryOnce, ENGINEER_TENDERS_VIEW_ALL_LEAVE_PRESERVE_ROUTE_NAMES } from '@/constants/viewAllTableCache'
import viewAllCommonTableSnapshotMixin from '@/mixins/viewAllCommonTableSnapshot.mixin'
import HKLButton from '@/controls/Button/HKLButton'
import { TENDER_NUMBER_TYPE } from '@/api/tenders/consts'
import ConfirmCreate from '@/modules/tenders/ConfirmCreate'
import HKLRemindModal from '@/controls/Modal/HKLRemindModal.vue'
import { useRefModal } from '@/controls/Modal/modal.hook'
import { useUserSiteOptions } from '../hook'

const VIEW_ALL_SNAPSHOT_MIXINS = [viewAllCommonTableSnapshotMixin]

const sortingAttributes = {
  awardInfo: {
    amount: null,
  },
  prInfo: {},
  generalInfo: {
    status: null,
    awardedDate: {
      gte: null,
      lte: null,
    },
    closingInfo: {
      gte: null,
      lte: null,
    },
  },
}

export default {
  name: 'TendersViewAll',
  /** @see `@/mixins/viewAllCommonTableSnapshot.mixin.js` */
  viewAllTableSnapshotId: 'view-all-component-tenders',
  viewAllTableLeavePreserveRouteNames: ENGINEER_TENDERS_VIEW_ALL_LEAVE_PRESERVE_ROUTE_NAMES,
  components: {
    HKLButton,
    Sticky,
    DatePicker,
    HKLSelect,
    CommonTable,
    AdvanceSearchCollapse,
    HKLInput,
    HKLDateRange,
    ConfirmCreate,
    HKLRemindModal,
  },
  mixins: VIEW_ALL_SNAPSHOT_MIXINS,
  setup () {
    const { proxy: that } = getCurrentInstance()
    let returnData = {
      canExportSearchResults: computed(() => aclHelper.getAccess(aclPermsType.tenders.exportViewAll)),
    }
    const defaultOrderKey = 'awardedDate'
    const orderKey = ref('awardedDate')
    const reverse = ref(true)
    const invalidClosingDate = ref(false)
    const advanceConditions = ref({})
    const getTableOrder = (key, order) => {
      orderKey.value = key
      reverse.value = order
    }
    const exportTenders = () => {
      return exportAllTenders({
        tenderCacheKey: DATA_CACHE_KEYS.VIEW_ALL_TENDER,
        orderInfo: {
          orderbyAttr: orderKey.value,
          reverse: Number(reverse.value),
        },
      })
    }
    const invalidClosingDateClass = computed(() => {
      return { 'error-highlight': unref(invalidClosingDate) }
    })
    const invalidClosingTimeClass = computed(() => {
      const result = unref(invalidClosingDate)
      return [ result ? 'closing-time error-highlight' : 'closing-time' ]
    })
    const { data: siteOptions } = useUserSiteOptions(tenderRouteNames.tenders)

    const checkIsEmptyFilter = () => {
      const sortingAttributes = lodash.cloneDeep(that.sortingAttributes)
      if ((sortingAttributes.generalInfo.status || []).includes(ALL_VALUE)) {
        return false
      }
      const filterCriteria = that.getParsedAttributes()
      const dateKeys = [
        'awardedDate',
        'closingInfo',
        'closingDateRange',
        'awardedDateRange',
        'issuedDate',
      ]
      for (const item of Object.values(filterCriteria)) {
        for (const [key, value] of Object.entries(item)) {
          const isArrOrStr = Array.isArray(value) || typeof value === 'string'
          if (dateKeys.includes(key) && (value.gte || value.lte)) {
            return false
          } else if (isArrOrStr && value.length > 0) {
            return false
          }
        }
      }
      return true
    }
    const { open: remindEmptyModalOpen, modal: remindEmptyModal } = useRefModal()
    returnData = Object.assign(returnData, {
      siteOptions,
      invalidClosingDateClass,
      invalidClosingTimeClass,
      exportTenders,
      getTableOrder,
      defaultOrderKey,
      advanceConditions,
      invalidClosingDate,
      remindEmptyModal,
      checkIsEmptyFilter,
      remindEmptyModalOpen,
    })
    return returnData
  },
  data () {
    const emptyValue = this.$t('emptyValue')
    const closingTimeOptions = ['--/--', '12:00 Noon', '17:00']
    let toDate = new Date(Date.now())
    toDate.setDate(toDate.getDate() - 1)

    return {
      emptyValue,
      extendTenders: [],
      closingDateSearch: false,
      closingTimeOptions,
      toDate,
      disabledDates: {to: toDate},
      newClosingDate: '',
      selectedClosingDate: null,
      selectedClosingTime: null,
      tryToConfirm: false,
      dateUtil,
      vendorId: '',
      currencyFormatter,
      /** Raw rows from View-all search API (not vuex `allLastSearchedTenders`). */
      tenderViewAllRows: [],
      localLastSearchCount: 0,
      cachedDisplayListLength: 0,
      interactionFunc: {
        'prNumber': this.navToPage,
        'prDescription': this.navToPage,
      },
      isVendorHistory: false,
      sortingAttributes: lodash.cloneDeep(sortingAttributes),
      extendClosingDateReason: '',
      contractor: {},
      searchCompaniesResult: [],
      invitedVendorName: '',
      advancedDateRange: {
        closingDateRange: [],
        awardedDateRange: [],
        issuedDate: [],
      },
      tableId: 'view-all-component-tenders',
    }
  },
  computed: {
    ...mapGetters('user', ['isHKlandUser', 'currentUser']),
    ...mapGetters('tables', ['getTableInfoById']),
    ...mapGetters('currencies', { prCurrencies: 'pr' }),
    closingTimeHklOptions () {
      return this.closingTimeOptions.map((value) => ({
        name: value,
        value,
      }))
    },
    companyNames () {
      return this.searchCompaniesResult.map(company => company.name)
    },
    userHelper () {
      return userHelper
    },
    shouldUseTableCache () {
      return !lodash.get(this.$route, ['query', 'vendorId'])
    },
    displayTotalCount () {
      return this.localLastSearchCount || this.cachedDisplayListLength
    },
    showCreateButton () {
      return aclHelper.getAccess(aclPermsType.tenders.createUpdate)
    },
    canExtendClosingDate () {
      return aclHelper.getAccess(aclPermsType.tenders.extendClosingDate)
    },
    parsedItems () {
      const parsedTenders = this.tenderViewAllRows.map(tender => {
        const purchases = getPurchases(tender.general)
        const totalAmount = lodash.sumBy(purchases, item => +(item.amount))
        const awardedVendor = lodash.get(purchases, '0.vendor.name')
        const numberType = lodash.get(tender, 'general.numberType')
        const poNumber = lodash.get(tender, 'general.poNumber')
        // The `toString` of PO/Contract Number is for the historical data
        const poNumberStr = numberType === TENDER_NUMBER_TYPE.PO
          ? typeof poNumber === 'number' ? poNumber.toString() : poNumber.join(', ')
          : ''
        const contractNumber = lodash.get(tender, 'general.contractNumber')
        const contractNumberStr = numberType === TENDER_NUMBER_TYPE.CONTRACT
          ? typeof contractNumber === 'number' ? contractNumber.toString() : contractNumber.join(', ')
          : ''
        const general = tender.general
        general.isRepresentative = tender.isRepresentative
        return {
          id: lodash.get(tender, 'general.id'),
          contractNumber: contractNumberStr,
          prNumber: lodash.get(tender, 'general.prNumber'),
          poNumber: poNumberStr,
          prDescription: lodash.get(tender, 'general.prDescription'),
          site: lodash.get(tender, 'general.site'),
          showQIndicator: showQueryIndicator(tender),
          status: parseStatusText(tender.general),
          closingDate: lodash.get(tender, 'general.closingDate'),
          awardedDate: lodash.get(tender, 'general.awardedDate'),
          picName: userHelper.getUserName(
            lodash.get(tender, 'general.manager.username'),
            lodash.get(tender, 'general.manager.displayName')),
          awardedAmount: totalAmount,
          currencyCode: lodash.get(tender, 'purchaseRequisition.currencyCode'),
          awardedVendor,
          category: lodash.get(tender, 'purchaseRequisition.category'),
          subCategory: lodash.get(tender, 'purchaseRequisition.subCategory'),
          general,
          hasAccess: tender.hasAccess,
          allowLink: tender.hasAccess,
        }
      })
      return parsedTenders
    },
    itemHeaders () {
      return [
        {
          queryName: ['generalInfo', 'prNumber'],
          name: 'prNumber',
          title: 'PR No.',
          defaultValue: this.emptyValue,
          inputType: 'input',
        },
        {
          queryName: ['generalInfo', 'prDescription'],
          name: 'prDescription',
          title: this.$t('projectViewAll.projectTitle'),
          defaultValue: this.emptyValue,
          inputType: 'input',
        },
        {
          queryName: ['generalInfo', 'site'],
          name: 'site',
          title: this.$t('projectViewAll.site'),
          defaultFilterValue: [],
          component: HKLMultiSelect,
          options: {
            attributes: {
              options: this.siteOptions || [],
            },
          },
        },
        {
          queryName: ['generalInfo', 'status'],
          name: 'status',
          title: this.$t('projectViewAll.status'),
          component: HKLMultiSelect,
          defaultFilterValue: [],
          options: {
            attributes: {
              includeAll: true,
              options: [
                ...Object.keys(tenderStatus),
                ...Object.keys(ADDENDUM_STATUS).map(rec => `A-${rec}`),
              ],
            },
          },
          searchable: true,
        },
        {
          queryName: ['generalInfo', 'closingInfo'],
          name: 'closingDate',
          inputType: 'date',
          defaultValue: this.emptyValue,
          singleDatePicker: true,
          datePickerCanPutTotop: false,
          disabledDate: false,
          title: this.$t('tender.closingDate'),
          dateFromPlaceholder: '',
        },
        {
          queryName: ['generalInfo', 'awardedDate'],
          name: 'awardedDate',
          defaultValue: this.emptyValue,
          singleDatePicker: true,
          datePickerCanPutTotop: false,
          inputType: 'date',
          dateFromPlaceholder: '',
          title: this.$t('tender.awardedDate'),
        },
        {
          queryName: ['generalInfo', 'manager'],
          name: 'picName',
          inputType: 'input',
          defaultValue: this.emptyValue,
          title: this.$t('tender.general.manager'),
        },
        {
          queryName: ['awardInfo', 'amount'],
          name: 'awardedAmount',
          component: HKLInputOperatorNumber,
          defaultValue: this.emptyValue,
          title: this.$t('projectViewAll.awardAmount'),
          formatRule: this.formatMoney,
          options: {
            listeners: {
              'keydown.Enter': this.queryTenders,
            },
          },
        },
        {
          queryName: ['prInfo', 'currencyCode'],
          name: 'currencyCode',
          title: this.$t('currency'),
          defaultValue: this.emptyValue,
          component: HKLMultiSelect,
          defaultFilterValue: [],
          options: {
            attributes: {
              options: lodash.cloneDeep(this.prCurrencies).sort(),
            },
          },
        },
        {
          queryName: ['awardInfo', 'name'],
          name: 'awardedVendor',
          title: 'Awarded Vendor',
          defaultValue: this.emptyValue,
          inputType: 'input',
        },
        {
          queryName: ['generalInfo', 'poNumber'],
          name: 'poNumber',
          defaultValue: this.emptyValue,
          inputType: 'input',
          title: this.$t('project.po.no'),
        },
        {
          queryName: ['generalInfo', 'contractNumber'],
          name: 'contractNumber',
          defaultValue: this.emptyValue,
          inputType: 'input',
          title: this.$t('project.contract.no'),
        },
        {
          queryName: ['prInfo', 'category'],
          name: 'category',
          defaultValue: this.emptyValue,
          title: this.$t('eCircular.category'),
          inputType: 'input',
        },
        {
          queryName: ['prInfo', 'subCategory'],
          name: 'subCategory',
          defaultValue: this.emptyValue,
          title: 'Sub-Category',
          inputType: 'input',
        },
      ]
    },
  },
  beforeMount () {
    if (this.shouldUseTableCache) {
      this.initAdvanceConditions()
    }
  },
  async mounted () {
    this.vendorId = this.$router.currentRoute.query.vendorId || ''
    if (this.vendorId) {
      this.isVendorHistory = true
      await this.queryTenders()
      return
    }
    const { didStripResumeSearch } = await consumeResumeSearchQueryOnce(
      this.$router,
      this.$route.query,
      tenderRouteNames.tenders,
    )
    if (didStripResumeSearch && !this.checkIsEmptyFilter()) {
      await this.queryTenders()
    }
  },
  destroyed () {
    if (this.extendClosingDateModal) {
      this.extendClosingDateModal.destroy()
      delete this.extendClosingDateModal
      $('#extendClosingDateModal').remove()
    }
  },
  methods: {
    ...mapActions('tenders', ['loadAllSearchedTenders']),
    ...mapActions('listNavigation', ['setListNavigationSequence', 'clearListNavigationSequence']),
    ...mapMutations('tables', {
      setTableInfoIntoStore: mutationTypes.TABLE_SET_TABLE_INFO,
    }),
    disableLink (item) {
      return !item.hasAccess
    },
    /**
     * Same idea as allTenantProjectsMixin `getTableNavigationEntries`:
     * row order must match CommonTable’s `formatList` (sort / tie-break), not only parent-computed lists.
     */
    getTenderViewAllNavigationRows () {
      const commonTable = this.$refs['view-all-component-tenders']
      let rows = []
      if (commonTable && Array.isArray(commonTable.formatList) && commonTable.formatList.length) {
        rows = commonTable.formatList
      } else if (Array.isArray(this.parsedItems) && this.parsedItems.length) {
        rows = this.parsedItems
      }
      return rows
    },
    navToPage (item) {
      this.commitViewAllTenderListNavigation()
      navToDetailPage(item.general, this.$router.currentRoute.name)
    },
    commitViewAllTenderListNavigation () {
      const entries = this.getTenderViewAllNavigationRows()
        .filter(row => row.allowLink !== false && row.general)
        .map(row => {
          const routeName = resolveNavToTenderDetailRouteName(row.general)
          if (!routeName) return null
          return { id: row.id, kind: LIST_NAV_KIND.TENDER_RECORD, routeName }
        })
        .filter(Boolean)
      this.setListNavigationSequence({ entries })
    },
    initAdvanceConditions () {
      const key = 'advanceConditions'
      this[key] = lodash.cloneDeep(lodash.get(this.getTableInfoById(this.tableId), key, {}))
      this.invitedVendorName = lodash.get(this[key], 'invitedVendorName', '')
      this.initAdvanceDateRange()
    },
    initAdvanceDateRange () {
      for (const key in this.advancedDateRange) {
        this.advancedDateRange[key] = lodash.get(this.advanceConditions, key, [])
      }
    },

    setTableInfo (tableInfo) {
      this.advanceConditions.invitedVendorName = this.invitedVendorName
      this.advanceConditions = Object.assign(this.advanceConditions, this.advancedDateRange)
      this.setTableInfoIntoStore({ ...tableInfo, advanceConditions: this.advanceConditions })
    },
    resetFilters () {
      this.vendorId = ''
      this.isVendorHistory = false
      this.closingDateSearch = false
      this.sortingAttributes = lodash.cloneDeep(sortingAttributes)
      this.advanceConditions = {}
      this.invitedVendorName = ''
      this.advancedDateRange = {
        closingDateRange: [],
        awardedDateRange: [],
        issuedDate: [],
      }
      this.clearListNavigationSequence()
      this.$nextTick(() => {
        const table = this.$refs['view-all-component-tenders']
        if (table) table.resetFilterUiOnly()
      })
    },
    extendClosingDate () {
      if (!this.extendClosingDateModal) {
        this.extendClosingDateModal = new Foundation.Reveal($('#extendClosingDateModal'), {
          closeOnEsc: false,
          closeOnClick: false,
        })
      }
      this.openAlertModal()
    },
    openAlertModal () {
      this.extendClosingDateModal.open()
    },
    closeAlertModal () {
      this.extendClosingDateReason = ''
      this.selectedClosingDate = null
      this.selectedClosingTime = null
      this.tryToConfirm = false
      this.invalidClosingDate = false
      this.extendClosingDateModal.close()
    },

    parseStatusText,
    navBack () {
      this.$router.goBack()
    },
    async queryTenders () {
      if (this.checkIsEmptyFilter()) return this.remindEmptyModalOpen()
      this.closingDateSearch = false
      if (this.sortingAttributes.generalInfo.closingInfo.gte) {
        // manually set closingDate range within selected date
        this.sortingAttributes.generalInfo.closingInfo.lte = dateUtil.getEndTimeOfDay(this.sortingAttributes.generalInfo.closingInfo.gte)
        this.closingDateSearch = true
      } else {
        this.sortingAttributes.generalInfo.closingInfo.lte = null
      }
      if (this.sortingAttributes.generalInfo.awardedDate.gte) {
        this.sortingAttributes.generalInfo.awardedDate.lte = dateUtil.getEndTimeOfDay(this.sortingAttributes.generalInfo.awardedDate.gte)
      } else {
        this.sortingAttributes.generalInfo.awardedDate.lte = null
      }

      try {
        this.$root.$children[0].$refs.loadingLayer.$emit('startLoading')
        this.tenderViewAllRows = []
        this.localLastSearchCount = 0
        const [tenders, lastSearchCount] = await this.loadAllSearchedTenders({
          query: this.getParsedAttributes(),
          diskCacheKey: DATA_CACHE_KEYS.VIEW_ALL_TENDER,
        })
        this.tenderViewAllRows = Array.isArray(tenders) ? tenders : []
        this.localLastSearchCount = typeof lastSearchCount === 'number'
          ? lastSearchCount
          : (this.tenderViewAllRows.length || 0)
      } finally {
        this.$root.$children[0].$refs.loadingLayer.$emit('stopLoading')
      }

      await this.$nextTick()
      this.commitViewAllTenderListNavigation()

      if (this.closingDateSearch) {
        // date-picker should disable dates earlier than the selected closingDate
        let newDate = new Date(Date.parse(this.sortingAttributes.generalInfo.closingInfo.gte) + dateUtil.ONE_DAY_MILLI)
        this.disabledDates = dateUtil.toMoment(newDate).isBefore(this.toDate) ? { to: this.toDate } : { to: newDate }
        this.getExtendTender()
      }
    },
    getDisplayListLength (length) {
      this.cachedDisplayListLength = length
    },
    getExtendTender () {
      this.extendTenders = []
      this.tenderViewAllRows.forEach(item => {
        if (item.general.status === tenderStatus.ISSUED || item.general.status === tenderStatus.READY) {
          this.extendTenders.push(lodash.cloneDeep(item))
        }
      })
      this.extendTenders.reverse()
    },
    async confirmExtend () {
      this.closingDateValidator()
      this.tryToConfirm = true
      if (!this.extendClosingDateReason || this.invalidClosingDate) {
        return true
      }

      const requestTemplate = {
        general: {
          closingDate: this.newClosingDate,
          extendClosingDateReason: this.extendClosingDateReason,
        },
      }
      const results = await Promise.all(
        this.extendTenders.map(async (item) => {
          item.general.closingDate = this.newClosingDate
          item.general.extendClosingDateReason = this.extendClosingDateReason
          const request = { ...requestTemplate }
          if (item.general.status === tenderStatus.READY) {
            request.general.status = tenderStatus.ISSUED
          }

          request.auditLogAction = ActionTypes.tenders.closingdate.extend
          try {
            await updateTender(request, item.general.id, false)
          } catch (err) {
            return false
          }
          await this.doSendEmail(item)
          return true
        }),
      )
      if (results.every(result => result)) {
        notification.fireNotification(true, this.$i18n.t('success'))
      }

      this.extendTenders = []
      this.closeAlertModal()
      this.resetFilters()
    },

    async doSendEmail (tender) {
      try {
        const vendorResults = await sendEmail(EmailTypes.tenders.extendNotifyVendor, {tender}, false)
        const engineerResults = await sendEmail(EmailTypes.tenders.extendNotifyEngineer, {tender}, false)
        const failedEmails = vendorResults.concat(engineerResults).filter(result => !result.isSuccess).map(result => result.to)
        if (failedEmails.length > 0) {
          const message = `${this.$i18n.t('tender.general.prNumber')} ` + tender.general.prNumber + ': ' + `${this.$i18n.t('error.sendmail.toVendors.500')}\n\t${failedEmails.join('\n\t')}`
          notification.fireNotification(false, message)
          return false
        }
      } catch (err) {
        notification.fireNotification(false, err.message)
        return false
      }
      return true
    },

    closingDateValidator () {
      this.invalidClosingDate = false
      if (!this.newClosingDate) {
        this.invalidClosingDate = true
        return true
      }
      const today = new Date()
      if (this.newClosingDate.getTime() - today.getTime() <= 0) {
        this.invalidClosingDate = true
      }
      const convertedClosingDateTime = dateUtil.convertDateToArray(this.newClosingDate, dateFormat, '-')
      const time = convertedClosingDateTime[1].trim()
      return time === '00:00'
    },
    updateClosingDate (selectedDay, selectedTime) {
      this.selectedClosingDate = selectedDay
      this.selectedClosingTime = selectedTime
      const emptyTime = !selectedTime || selectedTime === '--/--'
      if (!selectedDay || emptyTime) {
        this.newClosingDate = null
        return
      }
      const closingDate = dateUtil.toMoment(new Date(dateUtil.parse(selectedDay)))
      switch (selectedTime) {
        case this.closingTimeOptions[1]:
          closingDate.add(12, 'hours')
          break
        case this.closingTimeOptions[2]:
          closingDate.add(17, 'hours')
          break
        default:
      }
      this.newClosingDate = new Date(closingDate)
    },
    getDateRangeParsed (sortingAttributes) {
      const dateRange = lodash.cloneDeep(this.advancedDateRange)
      for (const key in dateRange) {
        const data = dateRange[key]
        if (data.some(date => !!date)) {
          lodash.set(sortingAttributes.generalInfo, key, {})
          sortingAttributes.generalInfo[key].gte = data[0] || null
          sortingAttributes.generalInfo[key].lte = data[1] || null
          this.advanceConditions[key] = data
        }
      }
      return sortingAttributes
    },
    getParsedAttributes () {
      let sortingAttributes = lodash.cloneDeep(this.sortingAttributes)
      if (sortingAttributes.generalInfo.status && sortingAttributes.generalInfo.status.includes(ALL_VALUE)) {
        delete sortingAttributes.generalInfo.status
      }
      if (this.isVendorHistory) {
        sortingAttributes.vendorId = this.vendorId
      }
      if (this.invitedVendorName) {
        sortingAttributes.generalInfo.invitedVendorName = this.invitedVendorName
        this.advanceConditions.invitedVendorName = this.invitedVendorName
      }
      sortingAttributes = this.getDateRangeParsed(sortingAttributes)

      return sortingAttributes
    },
    getConditionsCallBack (conditions) {
      this.itemHeaders.map(header => {
        const condition = conditions[header.name]
        if (condition) {
          const value = typeof condition.value === 'string' ? condition.value.trim() : condition.value
          switch (header.name) {
            case 'awardedAmount':
              this.sortingAttributes[header.queryName[0]][header.queryName[1]] = value
              break
            case 'awardedDate':
              this.sortingAttributes[header.queryName[0]][header.queryName[1]].gte = value[0] || null
              break
            case 'closingDate':
              this.sortingAttributes[header.queryName[0]][header.queryName[1]].gte = value[0] || null
              break
            case 'poNumber':
              let contractNumberHeaderSetting = this.itemHeaders.find(header => header.name === 'contractNumber')
              if (contractNumberHeaderSetting) {
                contractNumberHeaderSetting.disabledSearch = !!value
              }
              this.sortingAttributes[header.queryName[0]][header.queryName[1]] = value
              break
            case 'contractNumber':
              let poNumberHeaderSetting = this.itemHeaders.find(header => header.name === 'poNumber')
              if (poNumberHeaderSetting) {
                poNumberHeaderSetting.disabledSearch = !!value
              }
              this.sortingAttributes[header.queryName[0]][header.queryName[1]] = value
              break
            default:
              this.sortingAttributes[header.queryName[0]][header.queryName[1]] = value
              break
          }
        }
      })
    },
    formatMoney (value) {
      return currencyFormatter.format(value, this.$globalProps.getCurrency())
    },
  },
}
</script>

<style lang="scss" scoped src="styles/popup.scss"></style>
<style lang="scss" scoped src="styles/viewAll.scss"></style>
<style lang="scss" scoped src="styles/dashboard/style.scss"></style>
<style lang="scss" scoped src="styles/dashboard/datatable.scss"></style>
<style lang="scss" scoped>
.create-btn {
  display: inline-block;
}
.advance-search-group {
  margin: 0 20px 5px 20px;
  .ad-search-condition-label {
    font-weight: bold;
    text-transform: uppercase;
  }
  & ::v-deep .search-condtions {
    display: flex;

    .invited-vendor {
      width: 350px;
    }

    .closing-date-range,
    .awarded-date-range,
    .issue-date-range,
    .invited-vendor {
      margin-right: 20px;
    }
  }
  label {
    font-size: 14px;
  }
}
.view-all-component-tenders {
  margin: 0 20px;
  margin-bottom: 0;
  & ::v-deep .view-all-component-tenders_common-table {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 272px);
    min-height: unset;
    margin: 0;
    padding-bottom: 10px;
    .common-table-header-container {
      min-width: 2400px;
      background: #f8f8f8;
    }
    .common-table-content-container {
      padding-bottom: 0;
      tr:nth-child(odd) {
        background: white !important;
      }
      tr:nth-child(even) {
        background: #f8f8f8 !important;
      }
    }
    .site {
      max-width: 160px;
      min-width: 160px;
    }

    .status,
    .prNumber,
    .poNumber,
    .contractNumber,
    .category {
      max-width: 110px;
      min-width: 110px;
    }
    .currencyCode {
      max-width: 130px;
      min-width: 130px;
    }

    .poNumber,
    .contractNumber {
      max-width: 140px;
      min-width: 140px;
    }

    .picName,
    .awardedVendor,
    .closingDate,
    .awardedDate,
    .status {
      max-width: 180px;
      min-width: 180px;
    }
    .subCategory {
      max-width: 265px;
      min-width: 265px;
    }
    .awardedAmount {
      max-width: 190px;
      min-width: 190px;
      .multiselect__single {
        width: 100%;
      }
    }

    .prDescription {
      min-width: 190px;
      width: calc(100% - 2160px);
    }

    /* PMS-157 CommonTable filter th { min-width: 0 } overrides column widths; restore filter-row alignment. */
    .common-header-filter {
      th.prNumber,
      th.category {
        min-width: 110px;
        max-width: 110px;
      }
      th.site {
        min-width: 160px;
        max-width: 160px;
      }
      th.currencyCode {
        min-width: 130px;
        max-width: 130px;
      }
      th.poNumber,
      th.contractNumber {
        min-width: 140px;
        max-width: 140px;
      }
      th.status,
      th.picName,
      th.awardedVendor,
      th.closingDate,
      th.awardedDate {
        min-width: 180px;
        max-width: 180px;
      }
      th.awardedAmount {
        min-width: 190px;
        max-width: 190px;
      }
      th.subCategory {
        min-width: 265px;
        max-width: 265px;
      }
      th.prDescription {
        min-width: 190px;
        width: calc(100% - 2160px);
      }

      ::v-deep .hkl-select {
        min-width: 0;
        max-width: 100%;
      }
    }
  }
}
input[type=text] {
  padding-left: 10px;
}
.pr-number {
  min-width: 90px;
}
</style>
<style lang="scss">
#view-all-component {
  .nav-bar {
    max-width: 100%;
    margin: 0;
  }

  .inline-date-picker-block .vdp-datepicker {
    width: 130px!important;
    display: inline-block;
    min-height: 34px;
    vertical-align: bottom;
    input[type="text"] {
      margin: 0px;
    }
  }
  .multiselect__tags {
    .multiselect__content-wrapper {
      white-space: nowrap;
    }
  }
}

#extendClosingDateModal {
  width: 800px;
  .closing-time {
    margin: 0 0 0 25px;
  }
  .error-highlight {
    background-color: #f9ecea!important;
  }
  .error-highlight .multiselect__tags,
  .error-highlight .multiselect__input {
    background-color: #f9ecea;
  }
  .reason-box {
    margin: 10px 0;
  }
  table {
    margin-bottom: 0;
  }
}
</style>
